import React from 'react';
import {connect} from 'react-redux';
import {Field} from 'redux-form';
import PropTypes from 'prop-types';

import {gql, withApollo} from 'react-apollo';
import Form from '@appComponents/ReduxFormControls';
import {FiltersWrapper} from '@appComponents/HOCFiltersWrapper';
import mapModulesToProps from '@utils/mapModulesToProps';
import {
    PROPERTY_LICENCE_TYPE_SPORTRADAR, PROPERTY_LICENCE_TYPE_STAGE_SPORT, PROPERTY_LICENCE_TYPE_OTHER,
} from '@constants/variables';
import {contentCategoriesFilteredByLevelAndParents} from '@graphql/content';
import {GetTournamentsFilteredByTournamentCategories} from '@graphql/tournament/query';
import {GetSport, GetContentCategoryLevel1} from '@graphql/propertyLicence/query';
import {tournamentCategoriesFilteredBySports} from '@graphql/tournamentCategories';
import {sortDropdownOptionsAlphanumerically} from '@utils/sorters';
import {showMessageBox} from '@utils/messageBox';
import {SearchFilter} from '@utils/commonFilters/SearchFilter';
import {TiersFilter} from '@utils/commonFilters/TiersFilter';
import {ContentCategoriesLevel1Filter} from '@utils/commonFilters/contentCategories/ContentCategoriesLevel1Filter';
import {ContentCategoriesLevel2Filter} from '@utils/commonFilters/contentCategories/ContentCategoriesLevel2Filter';
import {ContentCategoriesLevel3Filter} from '@utils/commonFilters/contentCategories/ContentCategoriesLevel3Filter';
import {SportsFilter} from '@utils/commonFilters/SportsFilter';
import {TournamentsFilter} from '@utils/commonFilters/TournamentsFilter';
import {TournamentCategoriesFilter} from '@utils/commonFilters/TournamentCategoriesFilter';
import {cancelGraphqlDebouncedFetches, createGraphqlDebouncedFetch} from '@utils/graphqlDebounce';
import useStageCategories from '@modules/propertyLicence/utils/useStageCategories';
import stageCategoriesManagerPropTypes from '@modules/propertyLicence/utils/stageCategoriesManager';

export class PropertyLicenceFilters extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            hasStandardSport: false,
            hasStageSport: false,
            hasCategory: false,
            hasSelectedStandardSport: false,
            hasSelectedStageSport: false,
            hasSelectedCategory: false,
            hasSelectedTournamentCategory: false,
            hasSelectedSecondCategory: false,
            selectedTournamentCategories: [],
            selectedTournaments: [],
            tournamentCategories: [],
            contentCategoriesSecondLevel: [],
            contentCategoriesThirdLevel: [],
            sportsLoading: true,
            contentCategoriesLoading: true,
        };

        this.onTypeChange = this.onTypeChange.bind(this);
    }

    componentWillUnmount() {
        this.cancelDebouncedFetches();
    }

    cancelDebouncedFetches = () => {
        cancelGraphqlDebouncedFetches([
            this.fetchTournamentCategoriesDebounced,
            this.fetchTournamentsDebounced,
            this.fetchFirstStageCategoriesDebounced,
            this.fetchSecondStageCategoriesDebounced,
            this.fetchContentCategoriesSecondLevelDebounced,
            this.fetchContentCategoriesThirdLevelDebounced,
        ]);
    };

    fetchTournamentCategoriesDebounced = createGraphqlDebouncedFetch((sportIds) => {
        this.getTournamentCategoriesFromGraphQL(sportIds);
    });

    fetchTournamentsDebounced = createGraphqlDebouncedFetch((tournamentCategoryIds) => {
        this.getTournamentsFromGraphQL(tournamentCategoryIds);
    });

    fetchFirstStageCategoriesDebounced = createGraphqlDebouncedFetch(
        (sportIds, previousValue, setValue) => {
            this.props.stageCategoriesManager
                .fetchFirstStageCategories(sportIds)
                .then((firstStageCategories) => this.updateStageCategoriesSelection(
                    'firstStageCategories',
                    previousValue,
                    setValue,
                    firstStageCategories
                ));
        }
    );

    fetchSecondStageCategoriesDebounced = createGraphqlDebouncedFetch(
        (firstStageCategoryIds, setValue) => {
            const previousValue = this.props.stageCategoriesManager.secondStageCategoryValue;
            this.props.stageCategoriesManager
                .fetchSecondStageCategories(firstStageCategoryIds)
                .then((secondStageCategories) => this.updateStageCategoriesSelection(
                    'secondStageCategories',
                    previousValue,
                    setValue,
                    secondStageCategories
                ));
        }
    );

    fetchContentCategoriesSecondLevelDebounced = createGraphqlDebouncedFetch((firstLevelIds) => {
        this.getSecondLevelCategoriesFromGraphQL(firstLevelIds);
    });

    fetchContentCategoriesThirdLevelDebounced = createGraphqlDebouncedFetch((secondLevelIds) => {
        this.getThirdLevelCategoriesFromGraphQL(secondLevelIds);
    });

    getSelectedValues(selectedValues, values) {
        const newSelectedTournamentCategories = [];

        for (let selectedCategoryIndex = 0;
            selectedCategoryIndex < selectedValues.length;
            selectedCategoryIndex++) {
            for (let categoryIndex = 0; categoryIndex < values.length; categoryIndex++) {
                if (selectedValues[selectedCategoryIndex]
                    === values[categoryIndex].value) {
                    newSelectedTournamentCategories.push(values[categoryIndex].value);
                }
            }
        }

        return newSelectedTournamentCategories;
    }

    getTournamentCategoriesFromGraphQL = (sportIds) => {
        this.props.client.query({
            query: gql(tournamentCategoriesFilteredBySports),
            variables: {
                sports: sportIds,
            },
        }).then((result) => {
            const tournamentCategories = sortDropdownOptionsAlphanumerically(
                    result.data.tournamentCategories.mapDataForDropdownWithIntVal()
                ),
                selectedTournamentCategories = this.state.selectedTournamentCategories;

            this.setState(() => {
                return {
                    tournamentCategoriesLoading: false,
                    tournamentCategories: tournamentCategories,
                };
            });

            this.props.form.changeValue('tournamentCategories',
                this.getSelectedValues(selectedTournamentCategories, tournamentCategories)
            );
        }).catch((error) => {
            this.setState(() => {
                return {
                    tournamentCategoriesLoading: false,
                    tournamentCategories: [],
                };
            });

            showMessageBox(
                'propertyLicenceIndexMessage',
                'Failed to fetch tournament categories filter data',
                `${error}`,
                'error'
            );
        });
    };

    getSecondLevelCategoriesFromGraphQL = (firstLevelIds) => {
        this.props.client.query({
            query: gql(contentCategoriesFilteredByLevelAndParents),
            variables: {
                level: 2,
                parents: firstLevelIds,
            },
        }).then((result) => {
            const secondLevelCategories = sortDropdownOptionsAlphanumerically(
                    result.data.contentCategories.mapDataForDropdownWithIntVal()
                ),
                selectedSecondLevelCategories = this.state.selectedSecondLevelCategories;

            this.setState(() => ({
                contentCategoriesSecondLevelLoading: false,
                contentCategoriesSecondLevel: secondLevelCategories,
            }));

            this.props.form.changeValue('contentCategoriesLevel2',
                this.getSelectedValues(selectedSecondLevelCategories, secondLevelCategories)
            );

        }).catch((error) => {
            this.setState(() => {
                return {
                    contentCategoriesSecondLevelLoading: false,
                    contentCategoriesSecondLevel: [],
                };
            });

            showMessageBox(
                'propertyLicenceIndexMessage',
                'Failed to fetch 2nd level content categories filter data',
                `${error}`,
                'error'
            );
        });
    };

    getThirdLevelCategoriesFromGraphQL = (secondLevelIds) => {
        this.props.client.query({
            query: gql(contentCategoriesFilteredByLevelAndParents),
            variables: {
                level: 3,
                parents: secondLevelIds,
            },
        }).then((result) => {
            const thirdLevelCategories = sortDropdownOptionsAlphanumerically(
                    result.data.contentCategories.mapDataForDropdownWithIntVal()
                ),
                selectedThirdLevelCategories = this.state.selectedThirdLevelCategories;

            this.setState(() => ({
                contentCategoriesThirdLevelLoading: false,
                contentCategoriesThirdLevel: thirdLevelCategories,
            }));

            this.props.form.changeValue('contentCategoriesLevel3',
                this.getSelectedValues(selectedThirdLevelCategories, thirdLevelCategories)
            );

        }).catch((error) => {
            this.setState(() => {
                return {
                    contentCategoriesThirdLevelLoading: false,
                    contentCategoriesThirdLevel: [],
                };
            });

            showMessageBox(
                'propertyLicenceIndexMessage',
                'Failed to fetch 3rd level content categories filter data',
                `${error}`,
                'error'
            );
        });
    };

    getSportFromGraphQL = (isStandard, isStage) => {
        this.setState(() => ({
            sports: [],
            sportsLoading: true,
        }));

        this.props.client.query({
            query: GetSport,
            variables: {
                isStandard: isStandard,
                isStage: isStage,
            },
        }).then((result) => {
            this.setState(() => ({
                sports: sortDropdownOptionsAlphanumerically(result.data.sports),
                sportsLoading: false,
            }));
        }).catch((error) => {
            this.setState(() => ({
                sports: [],
                sportsLoading: false,
            }));

            showMessageBox(
                'clientMessage',
                'Failed to fetch sports filter data',
                `${error}`,
                'error'
            );
        });
    };

    getContentCategoryFromGraphQL = () => {
        this.setState(() => ({
            contentCategories: [],
            contentCategoriesLoading: true,
        }));

        this.props.client.query({
            query: GetContentCategoryLevel1,
        }).then((result) => {
            this.setState(() => ({
                contentCategories: sortDropdownOptionsAlphanumerically(result.data.contentCategories),
                contentCategoriesLoading: false,
            }));
        }).catch((error) => {
            this.setState(() => ({
                contentCategories: [],
                contentCategoriesLoading: false,
            }));

            showMessageBox(
                'clientMessage',
                'Failed to fetch 1st level content categories filter data',
                `${error}`,
                'error'
            );
        });
    };

    getTournamentsFromGraphQL = (tournamentCategoryIds) => {
        this.fetchAndSetDropdownData({
            query: GetTournamentsFilteredByTournamentCategories,
            variables: {tournamentCategories: tournamentCategoryIds},
            stateKeys: {loading: 'tournamentsLoading', data: 'tournaments', selected: 'selectedTournaments'},
            formField: 'tournaments',
            errorMessage: 'Failed to fetch tournament filter data',
            mapFunction: data => data.tournaments.mapDataForDropdownWithIntVal(),
        });
    };

    fetchAndSetDropdownData = async ({query, variables, stateKeys, formField, errorMessage, mapFunction}) => {
        try {
            this.setState({[stateKeys.loading]: true});

            const result = await this.props.client.query({query, variables});

            const data = sortDropdownOptionsAlphanumerically(mapFunction(result.data));

            const selected = this.state[stateKeys.selected];
            this.setState({[stateKeys.loading]: false, [stateKeys.data]: data});

            this.props.form.changeValue(formField, this.getSelectedValues(selected, data));
        } catch (error) {
            this.setState({[stateKeys.loading]: false, [stateKeys.data]: []});

            showMessageBox('propertyLicenceIndexMessage', errorMessage, `${error}`, 'error');
        }
    };

    resetTypeRelatedDropdownsFields = () => {
        this.props.form.changeValue('sports', null);
        this.props.form.changeValue('tournamentCategories', null);
        this.props.form.changeValue('tournaments', null);
        this.props.form.changeValue('contentCategoriesLevel1', null);
        this.props.form.changeValue('contentCategoriesLevel2', null);
        this.props.form.changeValue('contentCategoriesLevel3', null);
        this.props.form.changeValue('firstStageCategories', null);
        this.props.form.changeValue('secondStageCategories', null);
    };

    onTypeChange = (event, value) => {
        const type = parseInt(value),
            hasStandardSport = type === PROPERTY_LICENCE_TYPE_SPORTRADAR,
            hasStageSport = type === PROPERTY_LICENCE_TYPE_STAGE_SPORT,
            hasCategory = type === PROPERTY_LICENCE_TYPE_OTHER;

        this.cancelDebouncedFetches();
        this.resetTypeRelatedDropdownsFields();
        this.props.stageCategoriesManager.reset();

        const state = {
            hasSelectedStandardSport: false,
            hasSelectedStageSport: false,
            hasSelectedCategory: false,
            hasSelectedSecondCategory: false,
            hasSelectedTournamentCategory: false,
            hasStandardSport: hasStandardSport,
            hasStageSport: hasStageSport,
            hasCategory: hasCategory,
            sports: [],
            tournamentCategories: [],
            tournaments: [],
            contentCategories: [],
            contentCategoriesSecondLevel: [],
            contentCategoriesThirdLevel: [],
            sportsLoading: hasStandardSport || hasStageSport,
            contentCategoriesLoading: hasCategory,
            tournamentCategoriesLoading: false,
            tournamentsLoading: false,
            selectedSports: [],
            selectedContentCategories: [],
            selectedTournamentCategories: [],
            selectedTournaments: [],
            selectedSecondLevelCategories: [],
            selectedThirdLevelCategories: [],
        };

        if (hasStandardSport || hasStageSport) {
            this.getSportFromGraphQL(hasStandardSport, hasStageSport);
        }

        if (hasCategory) {
            this.getContentCategoryFromGraphQL();
        }

        this.setState(() => (state));
    };

    onTournamentCategoryChange = (event, value) => {
        this.cancelDebouncedFetches();

        if (value?.length) {
            this.setState(() => ({
                selectedTournamentCategories: value,
                hasSelectedTournamentCategory: true,
                tournaments: [],
                tournamentsLoading: true,
            }));

            this.fetchTournamentsDebounced(value);
        } else {
            this.setState(() => ({
                selectedTournamentCategories: [],
                hasSelectedTournamentCategory: false,
                tournaments: [],
                tournamentsLoading: false,
                selectedTournaments: [],
            }));

            this.props.form.changeValue('tournaments', null);
        }
    };

    onTournamentChange = (event, value) => {
        this.setState(() => ({
            selectedTournaments: value,
        }));
    };

    onSportChange = (event, value) => {
        this.cancelDebouncedFetches();

        this.setState(
            {
                hasSelectedStandardSport: false,
                hasSelectedStageSport: false,
                tournamentCategories: [],
                tournamentCategoriesLoading: false,
            },
            () => {
                if (value?.length && this.state.hasStageSport) {
                    const {
                        firstStageCategoryValue,
                        setFirstStageCategoryValue,
                        setFirstStageCategoriesLoading,
                    } = this.props.stageCategoriesManager;

                    setFirstStageCategoriesLoading(true);
                    this.setState(
                        {
                            selectedSports: value.map(Number),
                            hasSelectedStageSport: true,
                        },
                        () => this.fetchFirstStageCategoriesDebounced(
                            value.map(Number),
                            firstStageCategoryValue,
                            setFirstStageCategoryValue
                        )
                    );
                } else if (value?.length && this.state.hasStandardSport) {
                    this.setState(
                        {
                            selectedSports: value.map(Number),
                            hasSelectedStandardSport: true,
                        },
                        () => this.fetchTournamentCategoriesDebounced(value.map(Number))
                    );
                } else {
                    this.props.stageCategoriesManager.reset();

                    this.props.form.changeValue('tournamentCategories', null);
                    this.props.form.changeValue('tournaments', null);
                    this.props.form.changeValue('firstStageCategories', null);
                    this.props.form.changeValue('secondStageCategories', null);
                }
            }
        );
    };

    onCategoryChange = (event, value) => {
        this.cancelDebouncedFetches();

        let state = {
            hasSelectedCategory: false,
            contentCategoriesSecondLevel: [],
        };

        if (0 < value?.length) {
            state = {
                hasSelectedCategory: true,
                contentCategoriesSecondLevelLoading: true,
                selectedContentCategories: value.map(Number),
            };

            this.fetchContentCategoriesSecondLevelDebounced(value.map(Number));
        } else {
            this.props.form.changeValue('contentCategoriesLevel2', null);
        }

        this.setState(() => (state));
    };

    onContentCategorySecondLevelChange = (event, value) => {
        this.cancelDebouncedFetches();

        this.setState(() => ({
            selectedSecondLevelCategories: value,
        }));

        let state = {
            hasSelectedSecondCategory: false,
            contentCategoriesThirdLevel: [],
        };

        if (0 < value?.length) {
            state = {
                hasSelectedSecondCategory: true,
                contentCategoriesThirdLevelLoading: true,
            };

            this.fetchContentCategoriesThirdLevelDebounced(value.map(Number));
        } else {
            this.props.form.changeValue('contentCategoriesLevel3', null);
        }

        this.setState(() => (state));
    };

    onContentCategoryThirdLevelChange = (event, value) => {
        this.setState(() => ({
            selectedThirdLevelCategories: value,
        }));
    };

    updateStageCategoriesSelection = (formField, previousValue, setValue, options) => {
        const selectedValues = this.getSelectedValues(previousValue || [], options || []);

        setValue(selectedValues.length ? selectedValues : null);
        this.props.form.changeValue(formField, selectedValues.length ? selectedValues : null);
    };

    onFirstStageCategoryChange = (event, value) => {
        const selectedFirstStageCategories = (value || []).map(Number);
        this.cancelDebouncedFetches();

        const {
            setFirstStageCategoryValue,
            setSecondStageCategoryValue,
            setSecondStageCategories,
            setSecondStageCategoriesLoading,
        } = this.props.stageCategoriesManager;

        setFirstStageCategoryValue(selectedFirstStageCategories.length ? selectedFirstStageCategories : null);

        if (selectedFirstStageCategories.length) {
            setSecondStageCategoriesLoading(true);
            this.fetchSecondStageCategoriesDebounced(
                selectedFirstStageCategories,
                setSecondStageCategoryValue
            );
        } else {
            setSecondStageCategoriesLoading(false);
            setSecondStageCategoryValue(null);
            setSecondStageCategories([]);
            this.props.form.changeValue('secondStageCategories', null);
        }
    };

    onSecondStageCategoryChange = (event, value) => {
        this.props.stageCategoriesManager.handleSecondStageCategoryChange(event, {value: (value || []).map(Number)});
    };

    render() {
        const {
                licensors,
                licensees,
                propertyLicenceTypes,
                contentTiers,
                contentPackageTypes,
                contentManagers,
                accreditation,
                tvStreaming,
                archives,
                propertyLicenceStatuses,
            } = this.props.data,
            loading = this.props.loading,
            stageCategoriesManager = this.props.stageCategoriesManager;

        return (
            <div className='propertyLicence__filters filter__controls__container'>
                <SearchFilter
                    loading={loading}
                    filters={this.props.filters.PropertyLicence}
                    restoreFilterValues={this.props.restoreFilterValues}
                />
                <Field
                    component={Form.ReduxSelect}
                    name='licensors'
                    options={licensors}
                    disabled={loading}
                    placeholder='Licensor'
                    loading={loading}
                    search
                    setValue={this.props.restoreFilterValues(this.props.filters.PropertyLicence, 'licensors')}
                    multiple
                    selection
                />
                <Field
                    component={Form.ReduxSelect}
                    name='licensees'
                    options={licensees}
                    disabled={loading}
                    placeholder='Licensee'
                    loading={loading}
                    search
                    setValue={this.props.restoreFilterValues(this.props.filters.PropertyLicence, 'licensees')}
                    multiple
                    selection
                />
                <Field
                    component={Form.ReduxSelect}
                    name='propertyLicenceType'
                    className={'--customWidth --propertyLicenceType'}
                    options={propertyLicenceTypes}
                    disabled={loading}
                    placeholder='Type'
                    loading={loading}
                    setValue={(this.props.restoreFilterValues(
                        this.props.filters.PropertyLicence, 'propertyLicenceType'))?.toString()
                    }
                    selection
                    onChangeValue={this.onTypeChange}
                />
                <SportsFilter
                    sports={this.state.sports}
                    loading={this.state.sportsLoading || loading}
                    filters={this.props.filters.PropertyLicence}
                    restoreFilterValues={this.props.restoreFilterValues}
                    hidden={!this.state.hasStandardSport && !this.state.hasStageSport}
                    onChangeValue={this.onSportChange}
                />
                <Field
                    component={Form.ReduxSelect}
                    name='firstStageCategories'
                    hidden={!this.state.hasSelectedStageSport || !this.state.hasStageSport}
                    options={stageCategoriesManager.firstStageCategories}
                    disabled={loading}
                    placeholder='1st stage category'
                    loading={stageCategoriesManager.firstStageCategoriesLoading}
                    multiple
                    selection
                    search
                    setValue={(this.props.restoreFilterValues(this.props.filters.PropertyLicence, 'firstStageCategories'))}
                    onChangeValue={this.onFirstStageCategoryChange}
                />
                <Field
                    component={Form.ReduxSelect}
                    name='secondStageCategories'
                    hidden={!stageCategoriesManager.firstStageCategoryValue?.length}
                    options={stageCategoriesManager.secondStageCategories}
                    disabled={loading}
                    placeholder='2nd stage category'
                    loading={stageCategoriesManager.secondStageCategoriesLoading}
                    multiple
                    selection
                    search
                    setValue={(this.props.restoreFilterValues(this.props.filters.PropertyLicence, 'secondStageCategories'))}
                    onChangeValue={this.onSecondStageCategoryChange}
                />
                <TournamentCategoriesFilter
                    tournamentCategories={this.state.tournamentCategories}
                    loading={this.state.tournamentCategoriesLoading}
                    filters={this.props.filters.PropertyLicence}
                    restoreFilterValues={this.props.restoreFilterValues}
                    hidden={!this.state.hasSelectedStandardSport || !this.state.hasStandardSport}
                    onChangeValue={this.onTournamentCategoryChange}
                />
                <TournamentsFilter
                    tournaments={this.state.tournaments}
                    loading={this.state.tournamentsLoading}
                    filters={this.props.filters.PropertyLicence}
                    restoreFilterValues={this.props.restoreFilterValues}
                    hidden={!this.state.hasSelectedTournamentCategory}
                    onChangeValue={this.onTournamentChange}
                />
                <ContentCategoriesLevel1Filter
                    loading={this.state.contentCategoriesLoading || loading}
                    hidden={!this.state.hasCategory}
                    options={this.state.contentCategories}
                    filters={this.props.filters.PropertyLicence}
                    restoreFilterValues={this.props.restoreFilterValues}
                    onChangeValue={this.onCategoryChange}
                />
                <ContentCategoriesLevel2Filter
                    loading={this.state.contentCategoriesSecondLevelLoading}
                    hidden={!this.state.hasSelectedCategory}
                    options={this.state.contentCategoriesSecondLevel}
                    filters={this.props.filters.PropertyLicence}
                    restoreFilterValues={this.props.restoreFilterValues}
                    onChangeValue={this.onContentCategorySecondLevelChange}
                />
                <ContentCategoriesLevel3Filter
                    loading={this.state.contentCategoriesThirdLevelLoading}
                    hidden={!this.state.hasSelectedSecondCategory}
                    options={this.state.contentCategoriesThirdLevel}
                    filters={this.props.filters.PropertyLicence}
                    restoreFilterValues={this.props.restoreFilterValues}
                    onChangeValue={this.onContentCategoryThirdLevelChange}
                />
                <TiersFilter
                    contentTiers={contentTiers}
                    loading={loading}
                    filters={this.props.filters.PropertyLicence}
                    restoreFilterValues={this.props.restoreFilterValues}
                />
                <Field
                    component={Form.ReduxSelect}
                    name='contentPackageType'
                    options={contentPackageTypes}
                    disabled={loading}
                    placeholder='Content package type'
                    loading={loading}
                    multiple
                    setValue={this.props.restoreFilterValues(this.props.filters.PropertyLicence, 'contentPackageType')}
                    selection
                    search
                />
                <Field
                    component={Form.ReduxSelect}
                    name='contentManagerUser'
                    options={contentManagers}
                    disabled={loading}
                    placeholder='Content manager'
                    loading={loading}
                    multiple
                    setValue={this.props.restoreFilterValues(this.props.filters.PropertyLicence, 'contentManagerUser')}
                    selection
                    search
                />
                <Field
                    component={Form.ReduxSelect}
                    name='propertyLicenceStatus'
                    className={'--customWidth --propertyLicenceStatus'}
                    options={propertyLicenceStatuses}
                    disabled={loading}
                    placeholder='Status'
                    loading={loading}
                    setValue={(this.props.restoreFilterValues(
                        this.props.filters.PropertyLicence, 'propertyLicenceStatus'))?.toString()
                    }
                    selection
                />
                <Field
                    component={Form.ReduxSelect}
                    name='hasAccreditation'
                    className={'--customWidth --propertyLicenceHasAccreditation'}
                    options={accreditation}
                    disabled={loading}
                    placeholder='Accreditation?'
                    loading={loading}
                    setValue={this.props.restoreFilterValues(this.props.filters.PropertyLicence, 'hasAccreditation')}
                    selection
                />
                <Field
                    component={Form.ReduxSelect}
                    name='isTvStreaming'
                    className={'--customWidth --propertyLicenceTvStreaming'}
                    options={tvStreaming}
                    disabled={loading}
                    placeholder='TV Streaming?'
                    loading={loading}
                    setValue={this.props.restoreFilterValues(this.props.filters.PropertyLicence, 'isTvStreaming')}
                    selection
                />
                <Field
                    component={Form.ReduxSelect}
                    name='isArchived'
                    className={'--customWidth --propertyLicenceArchived'}
                    options={archives}
                    disabled={loading}
                    placeholder='Archive status'
                    loading={loading}
                    setValue={this.props.restoreFilterValues(
                        this.props.filters.PropertyLicence,
                        'isArchived',
                        0
                    )}
                    selection
                />
            </div>
        );
    }
}

PropertyLicenceFilters.defaultProps = {
    licensors: [],
    licensees: [],
    propertyLicenceTypes: [],
    contentTiers: [],
    contentPackageTypes: [],
    contentManagers: [],
    propertyLicenceStatuses: [],
    accreditation: [],
    isTvStreaming: [],
    archives: [],
    sports: [],
    tournamentCategories: [],
    contentCategoriesSecondLevel: [],
};

PropertyLicenceFilters.propTypes = {
    licensors: PropTypes.array.isRequired,
    licensees: PropTypes.array.isRequired,
    propertyLicenceTypes: PropTypes.array.isRequired,
    contentTiers: PropTypes.array.isRequired,
    contentPackageTypes: PropTypes.array.isRequired,
    contentManagers: PropTypes.array.isRequired,
    propertyLicenceStatuses: PropTypes.array.isRequired,
    accreditation: PropTypes.array.isRequired,
    isTvStreaming: PropTypes.array.isRequired,
    archives: PropTypes.array.isRequired,
    sports: PropTypes.array.isRequired,
    tournamentCategories: PropTypes.array.isRequired,
    contentCategoriesSecondLevel: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    client: PropTypes.object,
    form: PropTypes.object,
    MessageBox: PropTypes.object,
    data: PropTypes.object,
    filters: PropTypes.object,
    restoreFilterValues: PropTypes.func.isRequired,
    stageCategoriesManager: stageCategoriesManagerPropTypes,
};

PropertyLicenceFiltersWrapper.propTypes = {
    client: PropTypes.object,
    form: PropTypes.object,
    data: PropTypes.object,
};

const mapDispatchToProps = mapModulesToProps(['MessageBox']);

function PropertyLicenceFiltersWrapper(props) {
    const stageCategoriesManager = useStageCategories({
        client: props.client,
        propertyLicenceContents: props.data?.propertyLicenceContents,
    });

    return <PropertyLicenceFilters {...props} stageCategoriesManager={stageCategoriesManager} />;
}

const PropertyLicenceFiltersWithApollo = withApollo(PropertyLicenceFiltersWrapper);

export default FiltersWrapper(
    connect(null, mapDispatchToProps)(PropertyLicenceFiltersWithApollo),
    {
        name: 'PropertyLicence',
        initialValues: {isArchived: 0},
    }
);
